import Immutable from 'immutable';
import { createSelector } from 'reselect';
import urlResolver from 'edrolo/utils/URLResolver';
import { pathGenerators, reducerKeys } from './reducer';
import { STATUS, SAVING_STATUS } from '../constants';

/**
 * TODO:
 *
 * - Remove normalizr
 * - Convert selectors that only work with exam(detail) content recurse through exam object sections/questions
 * - Convert selectors that only interrogate an exam(detail) object to decouple from redux state
 */

const recurseFullSerial = (allExamQuestions, questionId) => {
  const question = allExamQuestions.get(questionId.toString());
  const serial = question && question.get('serial');
  const parentQuestionId = question && question.get('parent');

  return parentQuestionId
    ? `${recurseFullSerial(allExamQuestions, parentQuestionId)}${serial}`
    : serial;
};

// recursive helper for flattening exam questions
const recurseFlatten = (allExamQuestions, questions, level = 0) => {
  return (
    questions
      // get the actual question from the store
      .map((questionId) => allExamQuestions.get(questionId.toString()))
      // process each question
      .reduce((questionAcc, question) => {
        const children = question.get('children');

        // parents arent included, process the children and add to accumlator
        if (children.size > 0) {
          const descendentsFlattened = recurseFlatten(
            allExamQuestions,
            children,
            level + 1
          );
          return questionAcc.concat(descendentsFlattened);
        }

        // i'm not a parent, add me.
        return questionAcc.push(question.get('id'));
      }, Immutable.List())
  );
};

// selectors
const getAllQuestions = createSelector(
  (state) => state.exams.get(reducerKeys.examQuestions),
  (allQuestions) => {
    return allQuestions.map((question) =>
      question.set(
        'full_serial',
        recurseFullSerial(allQuestions, `${question.get('id')}`)
      )
    );
  }
);

export const getExam = (state, examId) =>
  state.exams.get(reducerKeys.exams).get(`${examId}`);

const getAllExamSections = (state) => state.exams.get(reducerKeys.examSections);

export const getExamLoadingStatus = (state, examId) => {
  const examStatus = state.exams.getIn(
    [reducerKeys.examLoadingStatus, `${examId}`],
    STATUS.UNINITIALIZED
  );

  return examStatus;
};

export const getExamLoadError = (state, examId) =>
  state.exams.getIn([reducerKeys.examLoadingErrors, `${examId}`]);

export const getQuestion = (state, questionId) =>
  getAllQuestions(state).get(`${questionId}`);
export const getExamSection = (state, sectionId) =>
  getAllExamSections(state).get(`${sectionId}`);
export const getFullSerial = (state, questionId) =>
  recurseFullSerial(getAllQuestions(state), `${questionId}`);

export const getSections = (state, examId) =>
  (getExam(state, examId) || Immutable.Map())
    .get('sections', Immutable.List())
    .map((sectionId) => getExamSection(state, `${sectionId}`));

export const getFlatQuestionIds = (state, examId) => {
  const exam = getExam(state, examId);

  if (!exam) {
    return null;
  }
  if (!exam.get('sections')) {
    return null;
  }

  const sections = getAllExamSections(state);
  const questions = getAllQuestions(state);

  return exam.get('sections').reduce((prev, sectionId) => {
    const section = sections.get(sectionId.toString());
    // flatten the tree of questions for this section
    const flattenedSectionQuestions = recurseFlatten(
      questions,

      section.get('questions')
    );
    // concatenate the flattened answers from each section
    return prev.concat(flattenedSectionQuestions);
  }, Immutable.List());
};

const getQuestionsByIds = (state, orderedQuestionIds) => {
  if (orderedQuestionIds === null) {
    return null;
  }

  const allExamQuestions = getAllQuestions(state);
  return orderedQuestionIds.reduce(
    (prev, questionId) =>
      prev.concat({
        [questionId.toString()]: allExamQuestions.get(`${questionId}`),
      }),
    Immutable.OrderedMap()
  );
};

// here we're producing an immutable OrderedMap..
// a map of questions indexed by questionId that's in question order and can be iterated over.
export const getFlatQuestions = (state, examId) => {
  const orderedQuestionIds = getFlatQuestionIds(state, examId);

  return getQuestionsByIds(state, orderedQuestionIds);
};

export const getFlatQuestionsForExamSection = (state, sectionId) => {
  const section = getExamSection(state, `${sectionId}`);
  const allExamQuestions = getAllQuestions(state);
  const flattenedSectionQuestionIds = recurseFlatten(
    allExamQuestions,
    section.get('questions')
  );
  return getQuestionsByIds(state, flattenedSectionQuestionIds);
};

export const getFlatQuestionsIndexedBySectionId = (state, examId) =>
  getSections(state, examId)
    .map((section) => `${section.get('id')}`)
    .reduce(
      (acc, sectionId) =>
        acc.set(sectionId, getFlatQuestionsForExamSection(state, sectionId)),
      Immutable.Map()
    );

// aggregate of the marks field for all questions
export const getTotalMarks = (state, examId) => {
  const questions = getFlatQuestions(state, examId);
  if (!questions) {
    return 0;
  }

  return questions.reduce(
    (acc, question) => acc + Number(question.get('marks')),
    0
  );
};

export const getFlattenedQuestionSerials = (state, examId) => {
  const flatQuestions = getFlatQuestions(state, examId);
  if (!flatQuestions) {
    return null;
  }

  const allExamQuestions = getAllQuestions(state);

  return flatQuestions.map((question) =>
    recurseFullSerial(allExamQuestions, question.get('id'))
  );
};

const isInstructionsTextSensible = (instructions = '') => {
  // Only alpha-numerics allowed
  const textInstructions = instructions.replace(
    // eslint-disable-next-line no-useless-escape
    /[~`!@#$%^&*()+={}\[\];:\'\"<>.,\/\\\?-_ ]/g,
    ''
  );

  const containsOnlyNumbers = /^\d+$/;
  if (textInstructions.match(containsOnlyNumbers)) {
    return false;
  }

  const containsVeryLittleText = textInstructions.length <= 20;
  if (containsVeryLittleText) {
    return false;
  }

  return true;
};

const buildQuestionTextInstructions = (
  examQuestions,
  questionId,
  textInstructions = ''
) => {
  const question = examQuestions.get(`${questionId}`);

  if (isInstructionsTextSensible(textInstructions)) {
    return textInstructions;
  }

  const instructions = `${question.get(
    'instructions_text'
  )} ${textInstructions}`;

  if (!question.get('parent') && instructions) {
    return instructions;
  }

  if (!question.get('parent') && !instructions) {
    return '[No Text Instructions]';
  }

  if (question.get('parent')) {
    return buildQuestionTextInstructions(
      examQuestions,
      question.get('parent'),
      instructions
    );
  }
};

export const getSensibleInstructionTextForQuestionId = (state, questionId) => {
  const examQuestions = state.exams.get('examQuestions');
  const instructionText = buildQuestionTextInstructions(
    examQuestions,
    questionId
  )
    .trim()
    .replace(/\s+/g, ' ');

  return instructionText;
};

export const getBreadcrumbs = (state, examId, classId) => {
  const yourCoursesUrl = urlResolver.getPath('courses-page');
  const exam = getExam(state, examId);
  const courseTitle = exam && exam.getIn(['branch_titles', 0]);
  const coursePageUrl = urlResolver.reverse('course-page', {
    classId,
  });

  const breadcrumbs = [
    { label: 'My subjects', url: yourCoursesUrl, anchor: true },
    { label: courseTitle, url: coursePageUrl },
  ];

  return breadcrumbs;
};

// Sitting/Responses Related
const getAllResponses = (state) =>
  state.exams.get(reducerKeys.responsesByQuestionId);
const getAllSolutions = (state) =>
  state.exams.get(reducerKeys.solutionsByQuestionId);
const getAllSittings = (state) => state.exams.get(reducerKeys.sittingsByExamId);
export const getResponse = (state, questionId) =>
  getAllResponses(state).get(`${questionId}`);
export const getSolution = (state, questionId) =>
  getAllSolutions(state).get(`${questionId}`);
export const getSitting = (state, examId) =>
  getAllSittings(state).get(`${examId}`);

export const getResponses = (state, examId) => {
  const responses = getAllResponses(state, examId);
  const questions = getFlatQuestions(state, examId);
  if (!responses || !questions) {
    return null;
  }

  return questions.map((question) =>
    responses.get(question.get('id').toString())
  );
};

// aggregate marks on all exam question responses
export const getTotalResponseMarks = (state, examId) => {
  const responses = getResponses(state, examId);
  if (!responses) {
    return null;
  }

  return responses.reduce((acc, response) => {
    if (response) {
      const marks = Number(response.get('marks_final'));
      return acc + marks;
    }
    return acc;
  }, 0);
};

export const getClassQuestionSetAggregates = (state, examId, classId) =>
  state.exams.getIn(
    [reducerKeys.classQuestionSetAggregates, examId, classId],
    Immutable.Map()
  );

export const getClassExamSittings = (state, examId, classId) =>
  state.exams.getIn(
    [reducerKeys.classExamSittings, examId, classId],
    Immutable.List()
  );

export const getClassExamSittingsStatus = (state, examId, classId) =>
  state.exams.getIn(
    ['classExamSittingsStatus', `${classId}-${examId}`],
    STATUS.UNINITIALIZED
  );

export const getClassQuestionResponses = (
  state,
  classId,
  examId,
  questionId
) => {
  const path = pathGenerators.classQuestionResponses(
    classId,
    examId,
    questionId
  );

  const responses = state.exams.getIn(path);
  return responses;
};

export const getChecklistItemIsSaving = (state, questionId) =>
  state.exams.getIn(
    pathGenerators.checklistItemSavingByQuestionId(questionId),
    SAVING_STATUS.IDLE
  ) === SAVING_STATUS.SAVING;

export const getGrokkingItemIsSaving = (state, questionId) =>
  state.exams.getIn(
    pathGenerators.grokkingItemSavingByQuestionId(questionId),
    SAVING_STATUS.IDLE
  ) === SAVING_STATUS.SAVING;
